import React, { useState } from "react";
import { createBrowserRouter, RouterProvider, Link, Navigate } from "react-router-dom";
import { createTheme, ThemeProvider } from "@mui/material/styles";

import { SharedPropsProvider } from "./HOC/SharedProps";
import { BindingGraphProvider } from "./HOC/BindingGraphProvider";
import { WebSocketProvider } from "./helpers/WebSocketContext";

import { googleLogout } from "@react-oauth/google";
import { jwtDecode } from "jwt-decode";

import { getToken, setToken, setUserData, clearToken } from "./helpers/tokenStorage";
import t from "./helpers/en";
import {
  NAV_ITEMS,
  USER_TYPE_SYSTEM,
} from "./helpers/constants";
import { getPermissionsSnapshot } from "./contexts/permissionsCache";

import MainLayout from "./pages/Main/Main";
import MinimalLayout from "./pages/Main/MinimalLayout";
import ErrorPage from "./pages/ErrorPage";
// Legacy /account, /account-users, /account-groups, /account-roles
// pages redirect to /access/*; the page components themselves
// aren't imported here anymore.
import {
  AccessLayout,
  AccessComingSoon,
  CmkPage,
  SupportAudit,
  SupportMcpTokensPage,
} from "./pages/Access";
import {
  DirectoryLayout,
  DirectoryAccounts,
  DirectoryAccountDetail,
  DirectoryUsers,
  DirectoryUserDetail,
  DirectoryGroups,
  DirectoryRoles,
  DirectoryRoleDetail,
} from "./pages/Directory";
import { SupportPage, SupportTicketDetailPage } from "./pages/Support";
import Login from "./pages/Login";
import CliLogin from "./pages/CliLogin";
import SsoHandoff from "./pages/SsoHandoff";
import AcceptNdaPage from "./pages/AcceptNdaPage";
import OnboardingPage from "./pages/OnboardingPage";
import RedeemInvitationPage from "./pages/RedeemInvitationPage";
import LinkGooglePage from "./pages/Settings/LinkGooglePage";
import Showcase from "./pages/Showcase/Showcase";
import ProtectedRoutes from "./ProtectedRoutes";
import Builds from "./pages/ViewBuilds";
import Images from "./pages/Images";
import ImageDetails from "./pages/ImageDetails";
import Services from "./pages/Services";
import ServiceAccounts from "./pages/ServiceAccounts";
import GitSecrets from "./pages/GitSecrets";
import Logs from "./pages/Logs";
import ConfigParams from "./pages/ConfigParams";
import Registries from "./pages/Registries";
import ManageApps from "./pages/ManageApps";
import AppDeploying from "./pages/AppDeploying";
import ManageDb from "./pages/ManageDb";
import Metrics from "./pages/Metrics";
import Costs from "./pages/Costs";
import LlmSpendPage from "./pages/LlmSpendPage";
import PlatformLimits from "./pages/PlatformLimits";
import DaariView from "./components/Daari/DaariView";
import DaariInstanceDetail from "./pages/DaariInstanceDetail";

// Brand-orange primary across both themes. Every default MUI
// component (Button, Tab, Switch, IconButton, Chip indicator…) now
// inherits the Clue2App orange instead of the legacy blue. Pages
// that want richer styling still compose via helpers/designSystem.
const lightTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: "#EA580C",
      light: "#F97316",
      dark: "#C2410C",
      activeLink: "#C2410C",
      text: "#fff",
      contrastText: "#fff",
    },
    background: {
      default: "#eef2f6",
    }
  },
});

const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: "#F97316",
      light: "#FB923C",
      dark: "#EA580C",
      activeLink: "#FB923C",
      text: "#fff",
      contrastText: "#fff",
    },
    background: {
      default: "#0f172a",
      paper: "#1e293b",
    }
  }
});

export default function App() {
  const [isDarkMode, setIsDarkMode] = useState(false);

  const handleThemeToggle = () => {
    setIsDarkMode(prev => !prev);
  };

  const [currentUser, setCurrentUser] = useState(() => {
    const token = getToken();
    return token && jwtDecode(token);
  });

  const onLoginResponse = (response, status, cb) => {
    if (status === 200) {
      //let`s try parse JWT
      const user = jwtDecode(response);

      setToken(response);
      setUserData(user);
      setCurrentUser(user);
      console.warn(process.env, "This are the env variables");
      cb && cb();
    } else if (status === 401 || status === 403) {
      onLogout();
    }
  };

  const onLogout = (cb) => {
    googleLogout();
    clearToken();
    // Other session-scoped UI state (selected project group, etc.) is
    // intentionally still ephemeral per-tab — only the auth token/user
    // data moved to localStorage.
    sessionStorage.clear();
    setCurrentUser(null);

    cb && cb();
  };

  // Enrich the JWT-derived currentUser with the dot-notation
  // entitlement keys held in the module-level permissions snapshot
  // (populated by <PermissionsProvider> on mount / focus / 60s poll).
  // NAV_ITEMS now switches its Directory + Accounts gates on those
  // keys via `hasEntitlement`; the snapshot is the source of truth
  // but we also thread it through props so tests and non-context
  // callers see the same shape. `hasEntitlement` falls back to the
  // snapshot if `effectivePermissions` is absent — the two paths
  // agree by construction.
  const navUser = currentUser
    ? {
        ...currentUser,
        effectivePermissions: getPermissionsSnapshot()?.permissions || [],
      }
    : currentUser;

  const router = createBrowserRouter([
    {
      path: "",
      errorElement: <ErrorPage />,
      children: [
        {
          path: "",
          element: <ProtectedRoutes {...{ currentUser }} />,
          children: [
            {
              path: "/",
              element: (
                <MainLayout
                  navItems={NAV_ITEMS(navUser)}
                  {...{ onLogout, currentUser, handleThemeToggle, isDarkMode }}
                />
              ),
              handle: {
                crumb: () => <Link to="">{t.home}</Link>,
              },
              children: [
                {
                  index: true,
                  element: <ManageApps {...{ currentUser }} />,
                },
                {
                  // Post-create landing page for "From Template" apps —
                  // polls build status and shows provisioning checkpoints
                  // while the first kpack build runs.
                  path: "apps/:id/deploying",
                  element: <AppDeploying {...{ currentUser }} />,
                  handle: {
                    crumb: () => <Link to="">{t.home}</Link>,
                  },
                },
                {
                  path: "config",
                  element: <ConfigParams {...{ currentUser }} />,
                  handle: {
                    crumb: () => <Link to="config">{t.configsAndSecrets}</Link>,
                  },
                },
                {
                  element: (
                    <ProtectedRoutes
                      {...{ currentUser }}
                      requiredRole={[USER_TYPE_SYSTEM, "ACCOUNT_ADMIN"]}
                    />
                  ),
                  children: [
                    {
                      path: "images",
                      element: <Images />,
                      handle: {
                        crumb: () => <Link to="images">{t.images}</Link>,
                      },
                    },
                    {
                      path: "logs",
                      element: <Logs />,
                      handle: {
                        crumb: () => <Link to="logs">{t.events}</Link>,
                      },
                    },
                    {
                      path: "git-secrets",
                      element: <GitSecrets />,
                      handle: {
                        crumb: () => <Link to="git-secrets">{t.gitRepos}</Link>,
                      },
                    },
                    {
                      path: "services",
                      element: <Services />,
                      handle: {
                        crumb: () => (
                          <Link to="services">{t.servicesItem}</Link>
                        ),
                      },
                    },
                    {
                      path: "registries",
                      element: <Registries />,
                      handle: {
                        crumb: () => (
                          <Link to="registries">{t.registries}</Link>
                        ),
                      },
                    },
                    {
                      path: "builds",
                      element: <Builds />,
                      handle: {
                        crumb: () => <Link to="builds">{t.builds}</Link>,
                      },
                    },
                  ],
                },
                {
                  element: (
                    <ProtectedRoutes
                      {...{ currentUser }}
                      requiredRole={[USER_TYPE_SYSTEM]}
                    />
                  ),
                  children: [
                    // builders, cluster-stacks, cluster-stores,
                    // kubernetes-clusters, domains routes removed —
                    // SYSTEM-only surfaces moved to admin app
                    // (clue2solve/aws-dashboard, Wave-1 migration).
                    {
                      path: "service-accounts",
                      element: <ServiceAccounts />,
                      handle: {
                        crumb: () => (
                          <Link to="service-accounts">{t.serviceAccounts}</Link>
                        ),
                      },
                    },
                    {
                      path: "manage-db",
                      element: <ManageDb />,
                      handle: {
                        crumb: () => (
                          <Link to="manage-db">
                            {t.manage} {t.database}
                          </Link>
                        ),
                      },
                    },
                    {
                      path: "platform-limits",
                      element: <PlatformLimits />,
                      handle: {
                        crumb: () => (
                          <Link to="platform-limits">{t.platformLimits}</Link>
                        ),
                      },
                    },
                  ],
                },
                {
                  // Daari is the canonical home for the service catalog
                  // (one tab per category). Preserve the old URL so
                  // bookmarks keep working.
                  path: "service-catalog",
                  element: <Navigate to="/daari" replace />,
                },
                {
                  path: "image/:id",
                  element: <ImageDetails {...{ currentUser }} />,
                },
                {
                  path: "metrics",
                  element: <Metrics {...{ currentUser }}/>,
                  handle: {
                    crumb: () => <Link to="metrics">{t.metrics}</Link>,
                  },
                },
                {
                  path: "costs",
                  element: <Costs {...{ currentUser }} />,
                  handle: {
                    crumb: () => <Link to="costs">Costs</Link>,
                  },
                },
                {
                  // SCK-632 — customer-facing LLM spend dashboard.
                  path: "llm/spend",
                  element: <LlmSpendPage {...{ currentUser }} />,
                  handle: {
                    crumb: () => <Link to="llm/spend">LLM Spend</Link>,
                  },
                },
                {
                  // SCK-635 slice B — Settings → Security → Link Google.
                  // Any signed-in user can open this to link (or re-link)
                  // Google, so no role gate — the endpoint on coord is
                  // scoped to the caller's own /me row.
                  path: "settings/security",
                  element: <LinkGooglePage />,
                  handle: {
                    crumb: () => (
                      <Link to="settings/security">Settings</Link>
                    ),
                  },
                },
                {
                  // SCK-549 PR-8 — dedicated /support page. Mounts the
                  // same SupportWidget component the floating widget
                  // uses (variant="full") alongside the caller's
                  // ticket list. Ticket detail is a sibling route.
                  path: "support",
                  element: <SupportPage />,
                  handle: {
                    crumb: () => <Link to="support">Support</Link>,
                  },
                },
                {
                  path: "support/tickets/:key",
                  element: <SupportTicketDetailPage />,
                  handle: {
                    crumb: () => <Link to="support">Support</Link>,
                  },
                },
                {
                  path: "daari",
                  element: <DaariView {...{ currentUser }} />,
                  handle: {
                    crumb: () => <Link to="daari">Daari</Link>,
                  },
                },
                {
                  // Deep-link to a single Daari Secret detail view.
                  // The same DaariView renders; the Secrets tab is
                  // pinned first, and useParams() inside DaariSecrets
                  // opens the detail dialog for the matched k8sName.
                  path: "daari/secrets/:k8sName",
                  element: <DaariView {...{ currentUser }} />,
                  handle: {
                    crumb: () => <Link to="daari">Daari</Link>,
                  },
                },
                {
                  // Per-catalog-instance detail page (postgres / awss3bucket
                  // / awsdynamodb / awssqs / llm-gateway). Chip clicks from
                  // the Apps dashboard land here via ?openInstance=…, and
                  // the Daari dashboard's "Manage bindings…" menu item on
                  // each service card links here directly.
                  path: "daari/:path/:name",
                  element: <DaariInstanceDetail />,
                  handle: {
                    crumb: () => <Link to="daari">Daari</Link>,
                  },
                },
                // Audit route removed — SYSTEM-only surface moved to
                // admin app (clue2solve/aws-dashboard, Wave-1
                // migration). This ProtectedRoutes(SYSTEM) wrapper is
                // left in place (now childless) as a marker for the
                // cleanup PR rather than deleted silently.

                // Legacy /account, /account-users, /account-groups,
                // /account-roles pages retired — the read/write
                // Directory surfaces now live at /directory/*. These
                // redirects preserve any bookmarks + external nav.
                // They live inside MainLayout so the primary sidebar
                // still renders while React Router resolves the
                // redirect target.
                {
                  path: "account",
                  element: <Navigate to="/directory/users" replace />,
                },
                {
                  path: "account-users",
                  element: <Navigate to="/directory/users" replace />,
                },
                {
                  path: "account-groups",
                  element: <Navigate to="/directory/groups" replace />,
                },
                {
                  path: "account-roles",
                  element: <Navigate to="/directory/roles" replace />,
                },
                {
                  // Legacy /members bookmarks — now Directory > Users.
                  path: "members",
                  element: <Navigate to="/directory/users" replace />,
                },
                // Legacy /access/members, /access/roles, /access/invitations
                // bookmarks land on their Directory equivalents.
                {
                  path: "access/members",
                  element: <Navigate to="/directory/users" replace />,
                },
                {
                  path: "access/roles",
                  element: <Navigate to="/directory/roles" replace />,
                },
                {
                  path: "access/invitations",
                  element: <Navigate to="/directory/accounts" replace />,
                },
                // /directory — canonical home for Accounts / Users /
                // Groups / Roles. Route gate is SYSTEM /
                // DIRECTORY_ADMIN / ACCOUNT_ADMIN; the Accounts
                // tab is additionally SYSTEM-gated by the nav rail
                // + will bounce a non-SYSTEM caller when PR-2 adds
                // per-route gating for /directory/accounts.
                //
                // SCK-638 — /directory + /access are children of the
                // single MainLayout wrapper above so the primary
                // NAV_ITEMS drawer stays mounted on every route.
                // DirectoryLayout / AccessLayout render only their
                // horizontal sub-nav + <Outlet /> inside the content
                // column; the persistent sidebar is a pure function of
                // this shared MainLayout mount.
                {
                  path: "directory",
                  element: (
                    <ProtectedRoutes
                      {...{ currentUser }}
                      requiredRole={[
                        USER_TYPE_SYSTEM,
                        "DIRECTORY_ADMIN",
                        "ACCOUNT_ADMIN",
                      ]}
                    />
                  ),
                  handle: {
                    crumb: () => <Link to="directory">Directory</Link>,
                  },
                  children: [
                    {
                      path: "",
                      element: <DirectoryLayout />,
                      children: [
                        {
                          index: true,
                          element: <Navigate to="accounts" replace />,
                        },
                        {
                          path: "accounts",
                          element: <DirectoryAccounts />,
                          handle: {
                            crumb: () => (
                              <Link to="directory/accounts">Accounts</Link>
                            ),
                          },
                        },
                        {
                          path: "users",
                          element: <DirectoryUsers {...{ currentUser }} />,
                          handle: {
                            crumb: () => (
                              <Link to="directory/users">Users</Link>
                            ),
                          },
                        },
                        {
                          path: "groups",
                          element: <DirectoryGroups />,
                          handle: {
                            crumb: () => (
                              <Link to="directory/groups">Groups</Link>
                            ),
                          },
                        },
                        {
                          path: "roles",
                          element: <DirectoryRoles />,
                          handle: {
                            crumb: () => (
                              <Link to="directory/roles">Roles</Link>
                            ),
                          },
                        },
                      ],
                    },
                    // Detail pages render OUTSIDE the layout so they
                    // get their own hero + back link, without the sub-nav
                    // rail. The parent /directory ProtectedRoutes gate
                    // still applies.
                    {
                      path: "roles/:id",
                      element: <DirectoryRoleDetail />,
                      handle: {
                        crumb: () => (
                          <Link to="directory/roles">Roles</Link>
                        ),
                      },
                    },
                    {
                      path: "users/:id",
                      element: <DirectoryUserDetail />,
                      handle: {
                        crumb: () => (
                          <Link to="directory/users">Users</Link>
                        ),
                      },
                    },
                    {
                      path: "accounts/:accountId",
                      element: <DirectoryAccountDetail />,
                      handle: {
                        crumb: () => (
                          <Link to="directory/accounts">Accounts</Link>
                        ),
                      },
                    },
                  ],
                },
                // /access — post-Directory-scaffold this section
                // carries the security-policy tabs only (SSO stub,
                // Support Audit, Security · CMK). Members /
                // Invitations / Roles routes above redirect to
                // /directory/*.
                {
                  path: "access",
                  element: <AccessLayout />,
                  handle: {
                    crumb: () => <Link to="access">Access</Link>,
                  },
                  children: [
                    {
                      index: true,
                      element: <Navigate to="support-audit" replace />,
                    },
                    {
                      path: "sso",
                      element: <AccessComingSoon slug="sso" />,
                      handle: {
                        crumb: () => <Link to="access/sso">SSO</Link>,
                      },
                    },
                    // SCK-631 — Access → Security → Customer-Managed Keys.
                    {
                      path: "security/cmk",
                      element: <CmkPage {...{ currentUser }} />,
                      handle: {
                        crumb: () => (
                          <Link to="access/security/cmk">
                            Customer-Managed Keys
                          </Link>
                        ),
                      },
                    },
                    {
                      path: "audit",
                      element: <AccessComingSoon slug="audit" />,
                      handle: {
                        crumb: () => <Link to="access/audit">Audit</Link>,
                      },
                    },
                    {
                      // SCK-569 — Layer 3 support-audit console.
                      path: "support-audit",
                      element: <SupportAudit {...{ currentUser }} />,
                      handle: {
                        crumb: () => (
                          <Link to="access/support-audit">Support Audit</Link>
                        ),
                      },
                    },
                    {
                      // SCK-549 PR-6 — caller-scoped Support MCP token
                      // mint / list / revoke. No role gate — any signed-
                      // in user manages their own tokens.
                      path: "support-mcp",
                      element: <SupportMcpTokensPage />,
                      handle: {
                        crumb: () => (
                          <Link to="access/support-mcp">Support MCP</Link>
                        ),
                      },
                    },
                  ],
                },
              ],
            },
          ],
        },
        // PUBLIC ROUTES
        {
          path: "/login",
          element: (
            <MinimalLayout
              className={{
                width: "fit-content",
                minHeight: "30em",
                display: "flex",
                alignItems: "center",
              }}
            />
          ),
          children: [
            {
              index: true,
              element: <Login {...{ onLoginResponse }} />,
            },
          ],
        },
        {
          path: "/cli-login",
          element: (
            <MinimalLayout
              className={{
                width: "fit-content",
                minHeight: "30em",
                display: "flex",
                alignItems: "center",
              }}
            />
          ),
          children: [
            {
              index: true,
              element: <CliLogin {...{ onLoginResponse }} />,
            },
          ],
        },
        // Cross-app SSO handoff — peer c2a apps (starting with
        // admin.clue2.app) redirect here with ?returnTo=... to
        // borrow the console's Google-OAuth session. No auth
        // wrapper: the component itself checks localStorage for
        // a valid token and bounces the caller either back home or
        // through /login first.
        {
          path: "/sso-handoff",
          element: (
            <MinimalLayout
              className={{
                width: "fit-content",
                minHeight: "20em",
                display: "flex",
                alignItems: "center",
              }}
            />
          ),
          children: [
            {
              index: true,
              element: <SsoHandoff />,
            },
          ],
        },
        // NDA acceptance landing — SCK-606 slice B. When any protected
        // fetch in helpers/actions.js catches
        //   403 {"error":"nda_required","currentVersion":"<v>"}
        // it stashes the caller's URL and redirects here. This route
        // lives OUTSIDE the ProtectedRoutes/PermissionsProvider wrapper
        // so mounting it doesn't fire /api/me/permissions (which would
        // itself 403 with nda_required and cause a redirect loop). The
        // page hits /api/nda/current (whitelisted on coord) and POSTs
        // /api/nda/accept using fetch() directly — bypassing the
        // interceptor — then navigates back to the stashed destination.
        {
          path: "/nda",
          element: (
            <MinimalLayout
              className={{
                width: "fit-content",
                minHeight: "30em",
                display: "flex",
                alignItems: "center",
              }}
            />
          ),
          children: [
            {
              index: true,
              element: <AcceptNdaPage />,
            },
          ],
        },
        // Invitation-redemption landing — SCK-697 child #1. The
        // invitation email built by coord's
        //   InvitationsService.buildRedemptionUrl
        // ships URLs shaped
        //   https://console.clue2.app/redeem?token=<raw>
        // Before this route existed, clicking one hit "Not Found" and
        // blocked the entire customer signup flow. The page is public
        // (no auth), mounted OUTSIDE ProtectedRoutes/PermissionsProvider
        // so we never fire /api/me/* against a visitor with no token
        // and drop them onto /login before they can redeem.
        {
          path: "/redeem",
          element: (
            <MinimalLayout
              className={{
                width: "fit-content",
                minHeight: "30em",
                display: "flex",
                alignItems: "center",
              }}
            />
          ),
          children: [
            {
              index: true,
              element: <RedeemInvitationPage />,
            },
          ],
        },
        // Onboarding landing — SCK-706 P0. When any protected fetch
        // in helpers/actions.js catches
        //   403 {"error":"no_membership", ...}
        // (authenticated caller, no account/workspace membership yet)
        // it redirects here. Public route mounted OUTSIDE
        // ProtectedRoutes/PermissionsProvider so opening /onboarding
        // never fires /api/me/permissions — which would 403
        // no_membership again and cause a redirect loop. The page
        // offers two forward paths: paste an invitation link
        // (/redeem?token=...) or POST /api/invitations/request for
        // operator-assigned access.
        {
          path: "/onboarding",
          element: (
            <MinimalLayout
              className={{
                width: "fit-content",
                minHeight: "30em",
                display: "flex",
                alignItems: "center",
              }}
            />
          ),
          children: [
            {
              index: true,
              element: <OnboardingPage />,
            },
          ],
        },
        // Public showcase — no auth wrapper, ships its own top bar
        // with a link back to clue2app.ai (the platform marketing site).
        // Coordinator endpoint /api/showcase/entries is whitelisted in
        // SecurityConfig; SYSTEM-admin moderation controls render
        // inline if the visitor happens to be signed in as SYSTEM.
        {
          path: "/showcase",
          element: <Showcase />,
        },
      ],
    },
  ]);

  return (
    <ThemeProvider theme={isDarkMode ? darkTheme : lightTheme}>
      <SharedPropsProvider>
        <BindingGraphProvider>
          <WebSocketProvider>
            <RouterProvider {...{ router }} />
          </WebSocketProvider>
        </BindingGraphProvider>
      </SharedPropsProvider>
    </ThemeProvider>
  );
}
